// Pure helpers for the Retention page (goals/admin-panel/DESIGN.md "Growth
// and Retention pages"): cohort shares and shades, and the activation funnel.
import type { AdminSchemas } from "@10twenty/api/admin";

export type AnalyticsCohorts = AdminSchemas["AnalyticsCohorts"];
export type AnalyticsFunnel = AdminSchemas["AnalyticsFunnel"];
export type CohortCell = AdminSchemas["AnalyticsCohortCell"];

/** A cell's share of its cohort active that week; null when unbuilt or the cohort was empty. */
export function cohortShare(cell: CohortCell | null): number | null {
  if (!cell || cell.size === 0) return null;
  return cell.active / cell.size;
}

/** Shades of the sequential ramp, 0 (none) to `SHADES` - 1 (everyone). */
export const SHADES = 7;
export function shade(share: number | null): number | null {
  if (share === null) return null;
  return Math.min(SHADES - 1, Math.max(0, Math.ceil(share * (SHADES - 1))));
}

/** The grid's widest row (the oldest cohort's), so every row gets that many columns. */
export function cohortColumns(data: AnalyticsCohorts): number {
  return Math.max(0, ...data.cohorts.map((cohort) => cohort.weeks.length));
}

export interface FunnelStep {
  key: "signed_up" | "in_circle" | "first_fix" | "sharing_member" | "active_week2";
  label: string;
  users: number;
  /** The step's base: the step before it (week 2: those of it whose week 2 has ended). */
  base: number | null;
  /** Of the base, and of all sign-ups (null without any). */
  ofPrevious: number | null;
  ofStart: number | null;
}

const STEPS: { key: FunnelStep["key"]; label: string }[] = [
  { key: "signed_up", label: "Signed up" },
  { key: "in_circle", label: "In a circle" },
  { key: "first_fix", label: "First fix" },
  { key: "sharing_member", label: "A second sharing member" },
  { key: "active_week2", label: "Active in week 2" },
];

/**
 * The funnel's steps, each counted among those who passed the one before.
 * Week 2 is a share of `week2_due` (those whose week 2 has ended); its
 * share of all sign-ups is that share times the previous step's.
 */
export function funnelSteps(funnel: AnalyticsFunnel): FunnelStep[] {
  const ratio = (part: number, whole: number | null) => (whole ? part / whole : null);
  return STEPS.map(({ key, label }, i) => {
    const users = funnel[key];
    if (i === 0) return { key, label, users, base: null, ofPrevious: null, ofStart: funnel.signed_up ? 1 : null };
    const previous = STEPS[i - 1]!.key;
    const base = key === "active_week2" ? funnel.week2_due : funnel[previous];
    // Week 2: the share of the previous step active then, applied to that step's share of sign-ups.
    const ofStart =
      key === "active_week2"
        ? (() => {
            const reached = ratio(funnel.sharing_member, funnel.signed_up);
            const week2 = ratio(users, funnel.week2_due);
            return reached === null || week2 === null ? null : reached * week2;
          })()
        : ratio(users, funnel.signed_up);
    return { key, label, users, base, ofPrevious: ratio(users, base), ofStart };
  });
}
